/**
 * First-party marketing attribution capture (C3).
 *
 * On landing, persist eligible UTMs / ad click ids / referrers in a
 * tenant-scoped journey (90 days), alongside `rawParams` — the landing's whole
 * query string, so a channel's next parameter is captured without a release.
 * Every landing appends its own touch, so a visitor who arrives from Meta and
 * returns from Google days later carries both, each with its own `capturedAt`.
 * Signup and booking attach the journey so the backend can append each observed
 * touch exactly once by `bundleId`.
 *
 * Everything is wrapped so attribution can never break the booking flow.
 */
import { stripWaitlistIdFromUrl } from "@/lib/booking-link-privacy";

import { getTenantKey } from "@/stores/tenant-storage-helpers";
import { useTenantStore } from "@/stores/tenant-store";

export interface MarketingTouch {
  bundleId: string;
  capturedAt: string; // ISO — when this touch was observed
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmId?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
  pageUrl?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  metaCampaignId?: string;
  metaAdsetId?: string;
  metaAdId?: string;
  /** Google ValueTrack: {campaignid}, {adgroupid}, {creative}. */
  googleAdsCampaignId?: string;
  googleAdsAdgroupId?: string;
  googleAdsCreativeId?: string;
  /** Every query parameter of the landing URL, including the mapped ones. */
  rawParams?: Record<string, string>;
}

/** Backward-compatible transport shape. Top-level fields remain first touch. */
export interface MarketingBundle extends MarketingTouch {
  touches?: MarketingTouch[];
  // Patient referral attribution from ?referring_pt=...&referral_program_id=...
  referringPt?: string;
  referralProgramId?: string;
  // ISO time the referral link was followed. Signup measures the attribution
  // window from here, not from signup time.
  referralCapturedAt?: string;
}

const STORAGE_BASE_KEY = "decoda-marketing-attribution";
const BUNDLE_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const MAX_TOUCHES = 20;
// A repeat landing from the same ad is only the same touch inside one visit.
// Beyond this the visitor came back, which is its own touchpoint.
const SESSION_WINDOW_MS = 30 * 60 * 1000;
// Mirrors the backend caps in decoda/schemas/marketing_attribution.py.
const MAX_RAW_PARAMS = 50;
const MAX_RAW_PARAM_KEY_LENGTH = 64;
const MAX_RAW_PARAM_VALUE_LENGTH = 512;

const TOUCH_STRING_FIELDS = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmId",
  "utmTerm",
  "utmContent",
  "referrer",
  "landingPage",
  "pageUrl",
  "gclid",
  "wbraid",
  "gbraid",
  "fbclid",
  "fbc",
  "fbp",
  "metaCampaignId",
  "metaAdsetId",
  "metaAdId",
  "googleAdsCampaignId",
  "googleAdsAdgroupId",
  "googleAdsCreativeId",
] as const satisfies readonly (keyof MarketingTouch)[];

const PARAM_MAP: Record<string, keyof MarketingTouch> = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_id: "utmId",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  gclid: "gclid",
  wbraid: "wbraid",
  gbraid: "gbraid",
  fbclid: "fbclid",
  meta_campaign_id: "metaCampaignId",
  meta_adset_id: "metaAdsetId",
  meta_ad_id: "metaAdId",
  google_ads_campaign_id: "googleAdsCampaignId",
  google_ads_adgroup_id: "googleAdsAdgroupId",
  google_ads_creative_id: "googleAdsCreativeId",
};

const META_UTM_SOURCES = new Set([
  "meta",
  "facebook",
  "instagram",
  "fb",
  "ig",
  "msg",
  "an",
]);

const GOOGLE_UTM_SOURCES = new Set([
  "google",
  "google_ads",
  "googleads",
  "adwords",
  "youtube",
  "gdn",
]);

function hasGoogleClickId(touch: MarketingTouch): boolean {
  return Boolean(touch.gclid || touch.wbraid || touch.gbraid);
}

function isGoogleTouch(touch: MarketingTouch): boolean {
  const source = touch.utmSource?.trim().toLowerCase();
  return Boolean(
    touch.googleAdsCampaignId ||
    touch.googleAdsAdgroupId ||
    touch.googleAdsCreativeId ||
    hasGoogleClickId(touch) ||
    (source && GOOGLE_UTM_SOURCES.has(source)),
  );
}

function isMetaTouch(touch: MarketingTouch): boolean {
  const source = touch.utmSource?.trim().toLowerCase();
  return Boolean(
    touch.metaCampaignId ||
    touch.metaAdsetId ||
    touch.metaAdId ||
    touch.fbclid ||
    touch.fbc ||
    (source && META_UTM_SOURCES.has(source)),
  );
}

/**
 * Query keys never worth keeping in an attribution ledger. Matched per token
 * (`rwg_token` -> {rwg, token}) so a marketing key that merely contains one of
 * these survives. The backend applies the same list and is the authority; this
 * copy keeps a credential out of localStorage and off the wire to begin with.
 */
const SENSITIVE_PARAM_TOKENS = new Set([
  "apikey",
  "auth",
  "bearer",
  "birth",
  "credential",
  "credentials",
  "dob",
  "email",
  "jwt",
  "key",
  "mobile",
  "otp",
  "passwd",
  "password",
  "phone",
  "pwd",
  "secret",
  "sig",
  "signature",
  "ssn",
  "tel",
  "token",
]);

/**
 * camelCase is a word boundary too: lowercasing `authToken` first would leave
 * one token `authtoken` and store a credential. A capture-group sub rather than
 * a lookbehind, so this stays identical to the Python twin and parses in every
 * browser that reaches the booking page.
 */
function isSensitiveParam(key: string): boolean {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((part) => SENSITIVE_PARAM_TOKENS.has(part));
}

/**
 * Key names cannot cover a value that carries the identifier itself
 * (`?ref=jane@example.com`). Only the email shape is matched: a random token is
 * indistinguishable from a click id, and guessing there would drop gclids.
 */
const EMAIL_SHAPED_VALUE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

/**
 * The landing's whole query string, bounded. Untyped on purpose — the mapped
 * params are the ones we report on, and this keeps the rest (a channel's new
 * click id, `booking_method=lightbox`, a clinic's own tag) rather than
 * discarding what no field happens to match. Repeated keys keep the first
 * value, matching `params.get`.
 */
function rawParams(params: URLSearchParams): Record<string, string> {
  const collected: Record<string, string> = {};
  params.forEach((value, key) => {
    if (Object.keys(collected).length >= MAX_RAW_PARAMS) return;
    // Judge the whole key and value, then truncate: cutting first loses the
    // token that condemns them.
    const name = key.trim();
    const text = value.trim();
    if (
      !name ||
      !text ||
      isSensitiveParam(name) ||
      name.toLowerCase() === "waitlistid"
    )
      return;
    if (EMAIL_SHAPED_VALUE.test(text)) return;
    const bounded = name.slice(0, MAX_RAW_PARAM_KEY_LENGTH);
    if (bounded in collected) return;
    collected[bounded] = text.slice(0, MAX_RAW_PARAM_VALUE_LENGTH);
  });
  return collected;
}

function storageKey(): string {
  const tenant = useTenantStore.getState().getCurrentTenant();
  return getTenantKey(STORAGE_BASE_KEY, tenant);
}

function readCookie(name: string): string | undefined {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function isMarketingTouch(value: unknown): value is MarketingTouch {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const touch = value as Record<string, unknown>;
  if (
    typeof touch.bundleId !== "string" ||
    touch.bundleId.length < 8 ||
    touch.bundleId.length > 64 ||
    typeof touch.capturedAt !== "string" ||
    !Number.isFinite(Date.parse(touch.capturedAt))
  ) {
    return false;
  }
  if (touch.rawParams !== undefined && !isRawParams(touch.rawParams)) {
    return false;
  }
  return TOUCH_STRING_FIELDS.every(
    (field) => touch[field] === undefined || typeof touch[field] === "string",
  );
}

function isRawParams(value: unknown): value is Record<string, string> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function isMarketingBundle(value: unknown): value is MarketingBundle {
  if (!isMarketingTouch(value)) return false;
  const touches = (value as unknown as { touches?: unknown }).touches;
  return (
    touches === undefined ||
    (Array.isArray(touches) &&
      touches.length <= MAX_TOUCHES &&
      touches.every(isMarketingTouch))
  );
}

function readStored(): MarketingBundle | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const bundle = JSON.parse(raw) as unknown;
    if (!isMarketingBundle(bundle)) {
      localStorage.removeItem(storageKey());
      return null;
    }
    // capturedAt is anchored to the oldest touch, so a fresh referral riding an
    // older marketing journey must not be evicted with it — keep the bundle while
    // either the marketing journey or the referral capture is still within TTL.
    const marketingFresh =
      Date.now() - Date.parse(bundle.capturedAt) <= BUNDLE_TTL_MS;
    const referralFresh =
      bundle.referralCapturedAt !== undefined &&
      Number.isFinite(Date.parse(bundle.referralCapturedAt)) &&
      Date.now() - Date.parse(bundle.referralCapturedAt) <= BUNDLE_TTL_MS;
    if (!marketingFresh && !referralFresh) {
      localStorage.removeItem(storageKey());
      return null;
    }
    return bundle;
  } catch {
    return null;
  }
}

function toTouch(bundle: MarketingBundle): MarketingTouch {
  const { touches: _touches, ...touch } = bundle;
  return touch;
}

function storedTouches(bundle: MarketingBundle): MarketingTouch[] {
  // An absent `touches` is a pre-journey bundle whose top-level fields are the
  // single touch. An empty one is a referral-only visit that genuinely has no
  // touches — synthesizing one there would resurrect a signal-less touch as
  // journey[0] and blank the real first-touch fields on the next visit.
  return bundle.touches ?? [toTouch(bundle)];
}

// `rawParams` counts only for the ad platforms' campaign ids the backend reads:
// the booking pages carry their own parameters (`appointmentId`, `serviceId`),
// and counting those would file a touchpoint for every ordinary visit.
const CAMPAIGN_ID_PARAMS = [
  "hsa_cam",
  "gad_campaignid",
  "campaignid",
  "campaign_id",
];
// Other networks' click ids and Google's own click markers: a landing carrying
// only one of these (a TikTok in-app browser sends no referrer, Safari strips
// gclid but keeps gad_source) is still an ad click. `srsltid` tags Google's
// organic Shopping listings. Mirrors `_SIGNAL_PARAMS` in the backend schema.
const CLICK_ID_PARAMS = [
  "msclkid",
  "ttclid",
  "li_fat_id",
  "twclid",
  "dclid",
  "gclsrc",
  "gad_source",
  "srsltid",
];

function hasSignal(touch: MarketingTouch): boolean {
  return Boolean(
    [...CAMPAIGN_ID_PARAMS, ...CLICK_ID_PARAMS].some(
      (key) => touch.rawParams?.[key],
    ) ||
    touch.utmSource ||
    touch.utmMedium ||
    touch.utmCampaign ||
    touch.utmId ||
    touch.utmTerm ||
    touch.utmContent ||
    touch.gclid ||
    touch.wbraid ||
    touch.gbraid ||
    touch.fbclid ||
    touch.metaCampaignId ||
    touch.metaAdsetId ||
    touch.metaAdId ||
    touch.googleAdsCampaignId ||
    touch.googleAdsAdgroupId ||
    touch.googleAdsCreativeId ||
    touch.referrer,
  );
}

function hasOwnAdEvidence(touch: MarketingTouch): boolean {
  return Boolean(
    hasGoogleClickId(touch) ||
    touch.fbclid ||
    touch.utmSource ||
    CLICK_ID_PARAMS.some((key) => touch.rawParams?.[key]),
  );
}

/**
 * A Google click the site's tag kept, when the URL lost it, is this landing's
 * unless the landing already names a click or another network.
 */
function keepsGoogleClick(touch: MarketingTouch): boolean {
  const source = touch.utmSource?.trim().toLowerCase();
  return (
    !hasOwnAdEvidence(touch) ||
    (!hasGoogleClickId(touch) &&
      !touch.fbclid &&
      !CLICK_ID_PARAMS.some((key) => touch.rawParams?.[key]) &&
      Boolean(source && GOOGLE_UTM_SOURCES.has(source)))
  );
}

function sameAttributionTouch(a: MarketingTouch, b: MarketingTouch): boolean {
  return (
    a.gclid === b.gclid &&
    a.wbraid === b.wbraid &&
    a.gbraid === b.gbraid &&
    a.fbclid === b.fbclid &&
    a.utmSource === b.utmSource &&
    a.utmMedium === b.utmMedium &&
    a.utmCampaign === b.utmCampaign &&
    a.utmId === b.utmId &&
    a.utmTerm === b.utmTerm &&
    a.utmContent === b.utmContent &&
    a.metaCampaignId === b.metaCampaignId &&
    a.metaAdsetId === b.metaAdsetId &&
    a.metaAdId === b.metaAdId &&
    a.googleAdsCampaignId === b.googleAdsCampaignId &&
    a.googleAdsAdgroupId === b.googleAdsAdgroupId &&
    a.googleAdsCreativeId === b.googleAdsCreativeId &&
    a.referrer === b.referrer &&
    CLICK_ID_PARAMS.every((key) => a.rawParams?.[key] === b.rawParams?.[key])
  );
}

/** One landing as observed on a page: where it was, where it came from, which ad cookies it carried. */
export interface ObservedLanding {
  /** The landing's own id, when the page that saw it kept one; repeat submissions then dedupe it. */
  bundleId?: string;
  href: string;
  /** The referring page, already cleared when it was the same site. */
  referrer?: string;
  capturedAt?: string;
  fbp?: string;
  fbc?: string;
  /** The Google click the site's own Google tag kept, when the URL no longer carries it. */
  gclid?: string;
}

function newBundleId(): string {
  return typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

/**
 * The attributable touch one landing represents, or null for a direct visit.
 * Pure: the caller supplies the URL, referrer and cookies, so a page embedded
 * in another site can describe the host page's landing instead of its own.
 */
export function buildMarketingTouch(
  landing: ObservedLanding,
): MarketingTouch | null {
  const url = new URL(landing.href);
  const params = url.searchParams;
  const touch: MarketingTouch = {
    bundleId: landing.bundleId ?? newBundleId(),
    capturedAt: landing.capturedAt ?? new Date().toISOString(),
    landingPage: landing.href.split("?")[0],
    pageUrl: stripWaitlistIdFromUrl(landing.href),
  };

  for (const [param, field] of Object.entries(PARAM_MAP)) {
    const value = params.get(param)?.trim();
    if (value) (touch as unknown as Record<string, string>)[field] = value;
  }
  const collected = rawParams(params);
  if (Object.keys(collected).length) touch.rawParams = collected;

  const referrer = landing.referrer?.trim();
  if (referrer) touch.referrer = referrer;
  if (landing.fbp) touch.fbp = landing.fbp;
  if (landing.fbc) touch.fbc = landing.fbc;
  // The cookie can hold a Google click from weeks ago, so it only speaks for a
  // visit that brought no ad of its own; a TikTok click today stays TikTok's.
  if (landing.gclid && keepsGoogleClick(touch)) touch.gclid = landing.gclid;

  // Both platforms offer a campaign-id URL macro — Meta {{campaign.id}},
  // Google {campaignid} — and clinics wire either into the standard utm_id.
  // A Google click id settles ownership outright, so it is checked first:
  // a visitor who arrives from Google still carrying a Meta cookie would
  // otherwise file their Google campaign id in the Meta column.
  const campaignId =
    touch.utmId ||
    params.get("campaignid")?.trim() ||
    params.get("campaign_id")?.trim() ||
    undefined;
  if (isGoogleTouch(touch)) {
    touch.googleAdsCampaignId ??= campaignId;
    touch.googleAdsAdgroupId ??= params.get("adgroupid")?.trim() || undefined;
    touch.googleAdsCreativeId ??=
      params.get("creative")?.trim() ||
      params.get("creativeid")?.trim() ||
      undefined;
  } else if (isMetaTouch(touch)) {
    touch.metaCampaignId ??= campaignId;
    touch.metaAdsetId ??= params.get("adset_id")?.trim() || undefined;
    touch.metaAdId ??= params.get("ad_id")?.trim() || undefined;
  }
  return hasSignal(touch) ? touch : null;
}

/**
 * The journey a set of observed landings adds up to, oldest first, in the
 * shape signup, booking and lead-form payloads carry.
 */
export function buildMarketingBundle(
  landings: ObservedLanding[],
): MarketingBundle | undefined {
  const touches = landings
    .map((landing) => {
      try {
        return buildMarketingTouch(landing);
      } catch {
        return null; // an unparseable URL costs its own touch, not the journey
      }
    })
    .filter((touch): touch is MarketingTouch => touch !== null)
    .slice(-MAX_TOUCHES);
  return touches.length ? { ...touches[0], touches } : undefined;
}

/**
 * What decoda.js on a clinic's site adds to a link or frame pointing here: the
 * page the visitor landed on there, how they reached it, and the ad cookies
 * that site's tags set. The ad's own parameters ride on the URL as they came.
 */
const HOST_PARAMS = {
  host: "dc_host",
  referrer: "dc_ref",
  landingId: "dc_lid",
  capturedAt: "dc_ts",
  fbp: "dc_fbp",
  fbc: "dc_fbc",
  gclid: "dc_gclid",
} as const;

function landingId(value: string | null): string | undefined {
  return value && value.length >= 8 && value.length <= 64 ? value : undefined;
}

/** When the visitor landed on the clinic site, if it is a time that could be true. */
function landingTime(value: string | null): string | undefined {
  const at = value ? Date.parse(value) : NaN;
  const now = Date.now();
  return at <= now && now - at <= BUNDLE_TTL_MS
    ? new Date(at).toISOString()
    : undefined;
}

/**
 * The clinic-site landing a link or frame URL describes, or null when the
 * visitor came here directly. Its href is the host page with the ad's
 * parameters, so the touch reads as the landing it was, not as this page.
 */
export function hostLandingFromUrl(href: string): ObservedLanding | null {
  const url = new URL(href);
  const params = url.searchParams;
  const host = params.get(HOST_PARAMS.host);
  if (!host?.startsWith("http")) return null;
  const fbclid = params.get("fbclid");
  const fbc = params.get(HOST_PARAMS.fbc);
  const landing: ObservedLanding = {
    href: host,
    bundleId: landingId(params.get(HOST_PARAMS.landingId)),
    capturedAt: landingTime(params.get(HOST_PARAMS.capturedAt)),
    referrer: params.get(HOST_PARAMS.referrer) || undefined,
    fbp: params.get(HOST_PARAMS.fbp) || undefined,
    // The site's _fbc outlives the click that set it; it is this landing's only when it names its fbclid.
    fbc: fbclid && fbc?.endsWith(`.${fbclid}`) ? fbc : undefined,
    gclid: params.get(HOST_PARAMS.gclid) || undefined,
  };
  Object.values(HOST_PARAMS).forEach((key) => params.delete(key));
  const query = params.toString();
  return { ...landing, href: query ? `${host}?${query}` : host };
}

/**
 * The Google click a `_gl` cross-domain link carries. Google's tag on the
 * clinic's site adds `_gl` to links bound for domains on its list; each value
 * is base64url, `_gcl_aw` decoding to `GCL.<time>.<gclid>`. The checksum is
 * Google's to verify; reading the click needs none of it.
 */
export function gclidFromLinker(linker: string | null): string | undefined {
  if (!linker) return undefined;
  const parts = linker.split("*");
  const index = parts.indexOf("_gcl_aw");
  const encoded = index >= 0 ? parts[index + 1] : undefined;
  if (!encoded) return undefined;
  try {
    const decoded = atob(
      encoded.replace(/-/g, "+").replace(/_/g, "/").replace(/\./g, "="),
    );
    const fields = decoded.split(".");
    // `_gcl_aw` lives 90 days; only a click from this visit is this visit's.
    const clickedAt = Number(fields[1]) * 1000;
    if (fields.length < 3 || !(Date.now() - clickedAt < SESSION_WINDOW_MS)) {
      return undefined;
    }
    return fields.slice(2).join(".");
  } catch {
    return undefined;
  }
}

function outsideReferrer(): string | undefined {
  const referrer = document.referrer?.trim();
  // Same-origin referrers (in-app navigation) carry no attribution.
  return referrer && !referrer.startsWith(window.location.origin)
    ? referrer
    : undefined;
}

function isFramed(): boolean {
  return window.parent !== window;
}

/** This page's own landing, when no clinic site sent the visitor with one. */
function ownLanding(): ObservedLanding {
  return {
    href: window.location.href,
    // A frame's referrer is the clinic page hosting it, not where the visitor came from.
    referrer: isFramed() ? undefined : outsideReferrer(),
    gclid: gclidFromLinker(
      new URLSearchParams(window.location.search).get("_gl"),
    ),
  };
}

/**
 * Capture the current URL's attribution. Safe to call from multiple mount
 * points — it must run before any code strips UTM parameters from the URL.
 * The first observed touch remains at the top level for older API clients;
 * the `touches` array carries the full idempotent journey.
 */
export function captureMarketingAttribution(): void {
  if (typeof window === "undefined") return;
  // Ad cookies on this domain are attached when the journey is sent, not
  // here: they are shared by every clinic booking on it and outlive visits.
  recordLanding(hostLandingFromUrl(window.location.href) ?? ownLanding());
}

/**
 * Capture what decoda.js on the clinic page hosting this frame reports: the
 * same parameters it adds to links, sent by message because a frame already
 * loaded before the tag ran never got them on its URL.
 */
export function captureHostAttribution(params: Record<string, string>): void {
  try {
    const href = `${window.location.origin}/?${new URLSearchParams(params)}`;
    const landing = hostLandingFromUrl(href);
    if (landing) recordLanding(landing);
  } catch {
    // Attribution must never break the page.
  }
}

function recordLanding(landing: ObservedLanding): void {
  try {
    const touch = buildMarketingTouch(landing);
    if (touch?.referrer)
      touch.referrer = stripWaitlistIdFromUrl(touch.referrer);

    // Patient referral link params. A referral link may carry no marketing
    // signal, so these are persisted independently of the touch.
    const params = new URLSearchParams(window.location.search);
    const referringPt = params.get("referring_pt")?.trim();
    const referralProgramId = params.get("referral_program_id")?.trim();
    const referral =
      referringPt && referralProgramId
        ? { referringPt, referralProgramId }
        : undefined;
    // Pure direct visit with no referral link — nothing attributable.
    if (!touch && !referral) return;

    const stored = readStored();
    const touches = stored ? storedTouches(stored) : [];
    // Every landing is its own touchpoint — a visitor who returns from the same
    // ad days later converted twice. Only collapse the reloads and extra mount
    // points inside one visit, which are the same landing seen more than once.
    const capturedAt = touch?.capturedAt ?? new Date().toISOString();
    const repeatInSameVisit = touches.some(
      (existing) =>
        touch !== null &&
        sameAttributionTouch(existing, touch) &&
        Date.parse(capturedAt) - Date.parse(existing.capturedAt) <
          SESSION_WINDOW_MS,
    );
    // A referral-only visit carries no marketing signal and never joins the
    // journey, but its referral params still have to persist below.
    const isNewTouch = touch !== null && !repeatInSameVisit;
    if (!isNewTouch && !referral) return;

    const journey = isNewTouch
      ? [...touches, touch].slice(-MAX_TOUCHES)
      : touches;

    // Persist even a referral-only visit: base off the first touch if one
    // exists, otherwise synthesize a minimal bundle to carry the referral.
    const base: MarketingTouch =
      journey[0] ??
      ({
        bundleId: touch?.bundleId ?? newBundleId(),
        capturedAt,
      } as MarketingTouch);
    localStorage.setItem(
      storageKey(),
      JSON.stringify({
        ...base,
        touches: journey,
        referringPt: referral?.referringPt ?? stored?.referringPt,
        referralProgramId:
          referral?.referralProgramId ?? stored?.referralProgramId,
        // Anchor the window to this visit when the link was followed now;
        // otherwise preserve the earlier follow time.
        referralCapturedAt: referral ? capturedAt : stored?.referralCapturedAt,
      } satisfies MarketingBundle),
    );
  } catch {
    // Attribution must never break the page.
  }
}

/**
 * The newest touch with Meta's cookies, when they speak for it: `_fbc` only
 * when it holds that touch's own click, `_fbp` only inside the visit. A cookie
 * from another clinic's ad, or from weeks ago, would credit the wrong click.
 */
function withVisitCookies(bundle: MarketingBundle): MarketingBundle {
  const touches = storedTouches(bundle);
  const newest = touches[touches.length - 1];
  if (!newest) return bundle;
  const fbc = readCookie("_fbc");
  const fbp = readCookie("_fbp");
  const ownClick = Boolean(newest.fbclid && fbc?.endsWith(`.${newest.fbclid}`));
  const thisVisit =
    Date.now() - Date.parse(newest.capturedAt) < SESSION_WINDOW_MS;
  const withCookies: MarketingTouch = {
    ...newest,
    fbc: newest.fbc ?? (ownClick ? fbc : undefined),
    fbp: newest.fbp ?? (thisVisit ? fbp : undefined),
  };
  return {
    ...bundle,
    touches: [...touches.slice(0, -1), withCookies],
  };
}

/** The live bundle to attach to signup/booking payloads, if any. */
export function getMarketingBundle(): MarketingBundle | undefined {
  try {
    if (typeof window === "undefined") return undefined;
    const stored = readStored();
    return stored ? withVisitCookies(stored) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The journey led to a booking, purchase or lead and the backend has it, so
 * it is spent: the next conversion on this browser starts from its own touches.
 */
export function consumeMarketingAttribution(): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.removeItem(storageKey());
  } catch {
    // Attribution must never break the page.
  }
}

/** Ids other tools on a clinic's site gave the visit (Liine's session, GA4's client id). */
export type VisitParams = Record<string, string>;

const LIINE_SESSION_ID =
  /^(?:lnsg_)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The visit ids a URL carries. Liine appends its session as `lnsg` when it
 * follows a visitor onto another domain, which is how one reaches a hosted
 * lead form or the frame of an embedded one.
 */
export function visitParamsFromUrl(search: string): VisitParams {
  const lnsg = new URLSearchParams(search).get("lnsg")?.trim();
  return lnsg && LIINE_SESSION_ID.test(lnsg) ? { liine_session_id: lnsg } : {};
}
